import { Input } from '@heroui/input';
import { effectiveTierPrice, validateTierSelection, type TierInfo, type TierPrice } from '@/utils/tierPricing';

interface Props {
    tiers: TierInfo[];
    tierIds: string[];
    overrides: TierPrice[];
    onChange: (next: { tierIds: string[]; tierPrices: TierPrice[] }) => void;
    soldByTier?: Record<string, number>;
    step?: number;
    currencySymbol: string;
    fieldClassNames?: Record<string, string>;
    disabled?: boolean;
}

const noteFor = (name: string) => `Tier ${name} has sold tickets. Its price changes apply to future sales only.`;

export default function TicketTierPricing({ tiers, tierIds, overrides, onChange, soldByTier = {}, step, currencySymbol, fieldClassNames, disabled }: Props) {
    const selected = new Set(tierIds);
    const validation = validateTierSelection(tiers, tierIds, overrides, { step });
    const fmt = (n: number) => (n === 0 ? 'Free' : `${currencySymbol}${n.toFixed(2).replace(/\.00$/, '')}`);

    const toggleTier = (name: string, on: boolean) => {
        if (disabled) return;
        if (!on && (soldByTier[name] ?? 0) > 0) return;
        onChange({
            tierIds: on ? [...tierIds, name] : tierIds.filter(id => id !== name),
            tierPrices: on ? overrides : overrides.filter(o => o.tier !== name),
        });
    };

    const setOverride = (name: string, price: string) => {
        const rest = overrides.filter(o => o.tier !== name);
        onChange({ tierIds, tierPrices: [...rest, { tier: name, price }] });
    };

    return (
        <section aria-label="Tiers and pricing" className="flex flex-col gap-3">
            <div>
                <h4 className="text-sm font-semibold text-[#222] dark:text-nightTextSecondary">Tiers and pricing</h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-nightMuted">Choose where this ticket is valid. Each tier uses its base price unless you enter a different price here.</p>
            </div>
            <ul className="flex flex-col gap-2">
                {tiers.map(t => {
                    const on = selected.has(t.name);
                    const sold = soldByTier[t.name] ?? 0;
                    const entry = overrides.find(o => o.tier === t.name);
                    const effective = effectiveTierPrice(t, overrides, t.name);
                    const overridden = Boolean(entry && String(entry.price).trim() !== '');
                    return (
                        <li key={t.name} className="rounded border border-grey90 dark:border-nightBorder p-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={on}
                                        disabled={disabled || (on && sold > 0)}
                                        onChange={e => toggleTier(t.name, e.target.checked)}
                                        aria-label={`Valid in tier ${t.name}`}
                                    />
                                    <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} aria-hidden />
                                    <span className="truncate text-sm text-[#222] dark:text-nightTextSecondary" title={t.name}>{t.name}</span>
                                    <span className="text-xs text-gray-400 dark:text-nightMuted whitespace-nowrap">{t.seatCount} seats</span>
                                </label>
                                <span className="text-xs text-gray-500 dark:text-nightMuted whitespace-nowrap">
                                    {t.basePrice === undefined ? 'No base price' : `Base ${fmt(t.basePrice)}`}
                                </span>
                            </div>
                            {on && (
                                <div className="mt-3">
                                    <Input
                                        classNames={fieldClassNames}
                                        placeholder={t.basePrice === undefined ? 'Price...' : `Use base price (${fmt(t.basePrice)})`}
                                        aria-label={`Price override for tier ${t.name}`}
                                        value={entry?.price?.toString() ?? ''}
                                        onChange={e => setOverride(t.name, e.target.value)}
                                        isInvalid={Boolean(validation.errors[t.name])}
                                        errorMessage={validation.errors[t.name]}
                                        description={!validation.errors[t.name] && effective !== null ? `${overridden ? 'Override' : 'Base price'}: ${fmt(effective)}` : undefined}
                                    />
                                </div>
                            )}
                            {on && sold > 0 && (
                                <p className="mt-2 text-xs text-warning30 dark:text-warning60">{noteFor(t.name)}</p>
                            )}
                        </li>
                    );
                })}
            </ul>
            {validation.formError && <p className="text-xs text-danger">{validation.formError}</p>}
        </section>
    );
}
